import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostCategoryVideoMutation } from '@/services/queries/category-video';
import { PostMediaCategoryVideoRequest } from '@/services/request/category-video';
import { useTranslation } from 'react-i18next';
export interface CategoryVideoCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const CategoryVideoCreateModal = ({ isOpen, onClose, onDone }: CategoryVideoCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostCategoryVideoMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: PostMediaCategoryVideoRequest = {
        title: data.title,
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_category_video.componnets.category_video_create_modal.category_modal_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_category_video.componnets.category_video_create_modal.category_modal_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_category_video.componnets.category_video_create_modal.category_modal_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t('pages.application_category_video.componnets.category_video_create_modal.category_modal_title_label')}
              {...register('title')}
              helperText={t(
                'pages.application_category_video.componnets.category_video_create_modal.category_modal_title_helper_text',
              )}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_category_video.componnets.category_video_create_modal.category_modal_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_category_video.componnets.category_video_create_modal.category_modal_create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
